import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users, CreditCard, FileText, BarChart3, ArrowLeft, 
  Search, Ban, RefreshCw, TrendingUp, TrendingDown,
  Eye, Calendar, DollarSign, Activity, PieChart,
  UserPlus, Zap, Target, Crown, CheckCircle, Gift, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const PLANS = [
  { id: 'pack5', name: 'Pack 5 Leituras', price: 19.90 },
  { id: 'detailed10', name: 'Detalhadas 10 Temas', price: 49.90 },
  { id: 'advanced', name: 'Avançado', price: 99.00 }
];

const AdminDashboard = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [readings, setReadings] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activatingPlan, setActivatingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metricsRes, usersRes, subsRes, readingsRes] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/admin/users'),
        api.get('/admin/subscriptions'),
        api.get('/admin/readings')
      ]);
      setMetrics(metricsRes.data);
      setUsers(usersRes.data.users);
      setSubscriptions(subsRes.data.subscriptions);
      setReadings(readingsRes.data.readings);
      
      // Fetch pending receipts
      try {
        const receiptsRes = await api.get('/admin/receipts?status_filter=pending');
        setReceipts(receiptsRes.data.receipts);
      } catch (e) {
        console.error('Error fetching receipts:', e);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (userId, blocked) => {
    try {
      await api.put(`/admin/users/${userId}`, { blocked: !blocked });
      fetchData();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleResetFreeTrial = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, { reset_free_trial: true });
      fetchData();
    } catch (error) {
      console.error('Error resetting free trial:', error);
    }
  };

  const handleApproveReceipt = async (receiptId) => {
    try {
      await api.post(`/admin/receipts/${receiptId}/approve`);
      alert('Comprovante aprovado e plano ativado!');
      fetchData();
    } catch (error) {
      console.error('Error approving receipt:', error);
      alert('Erro ao aprovar comprovante');
    }
  };

  const handleRejectReceipt = async (receiptId) => {
    const reason = prompt('Motivo da rejeição:');
    if (!reason) return;
    
    try {
      await api.post(`/admin/receipts/${receiptId}/reject?reason=${encodeURIComponent(reason)}`);
      alert('Comprovante rejeitado');
      fetchData();
    } catch (error) {
      console.error('Error rejecting receipt:', error);
      alert('Erro ao rejeitar comprovante');
    }
  };

  const handleActivatePlan = async (userId, planId) => {
    if (!planId) return;
    try {
      setActivatingPlan(userId);
      await api.post('/admin/activate-plan', { user_id: userId, plan_id: planId });
      setSelectedPlan('');
      setActivatingPlan(null);
      fetchData();
      alert('Plano ativado com sucesso!');
    } catch (error) {
      console.error('Error activating plan:', error);
      alert('Erro ao ativar plano');
      setActivatingPlan(null);
    }
  };

  const handleSyncPayments = async () => {
    try {
      setLoading(true);
      const response = await api.post('/admin/sync-pending-payments');
      const { activated, total_pending_checked } = response.data;
      
      if (activated.length > 0) {
        alert(`Sincronização concluída!\n${activated.length} pagamentos ativados.\n\n${activated.map(a => `- ${a.user_email}: ${a.plan}`).join('\n')}`);
      } else {
        alert(`Nenhum pagamento aprovado encontrado.\n${total_pending_checked} assinaturas pendentes verificadas.`);
      }
      
      fetchData();
    } catch (error) {
      console.error('Error syncing payments:', error);
      alert('Erro ao sincronizar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2E5C55] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#8E8E93]">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="gradient-primary px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-2 text-white/80 hover:text-white transition-colors rounded-xl hover:bg-white/10"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-white/70 text-sm">Gerenciamento e métricas</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleSyncPayments}
              variant="outline"
              className="bg-green-500/20 border-green-400/30 text-white hover:bg-green-500/30"
              title="Verificar pagamentos aprovados no Mercado Pago"
            >
              <Zap className="h-4 w-4 mr-2" />
              Sincronizar Pagamentos
            </Button>
            <Button 
              onClick={fetchData}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-2xl p-1 mb-8 shadow-sm border border-[#E8E4D9]/50 flex-wrap">
            <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-[#2E5C55] data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl data-[state=active]:bg-[#2E5C55] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Usuários
            </TabsTrigger>
            <TabsTrigger value="receipts" className="rounded-xl data-[state=active]:bg-[#2E5C55] data-[state=active]:text-white relative">
              <FileText className="h-4 w-4 mr-2" />
              Comprovantes
              {receipts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {receipts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-xl data-[state=active]:bg-[#2E5C55] data-[state=active]:text-white">
              <CreditCard className="h-4 w-4 mr-2" />
              Assinaturas
            </TabsTrigger>
            <TabsTrigger value="readings" className="rounded-xl data-[state=active]:bg-[#2E5C55] data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Leituras
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Main Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2E5C55]/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#2E5C55]" />
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    +{metrics?.new_users_week || 0} esta semana
                  </span>
                </div>
                <p className="text-3xl font-bold text-[#1C1C1E]">{metrics?.total_users || 0}</p>
                <p className="text-sm text-[#8E8E93]">Total de usuários</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4A373]/10 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-[#D4A373]" />
                  </div>
                  <span className="text-xs bg-[#D4A373]/10 text-[#D4A373] px-2 py-1 rounded-full font-medium">
                    MRR
                  </span>
                </div>
                <p className="text-3xl font-bold text-[#1C1C1E]">{formatCurrency(metrics?.monthly_revenue || 0)}</p>
                <p className="text-sm text-[#8E8E93]">Receita mensal</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#4DB6AC]/10 flex items-center justify-center">
                    <Crown className="h-6 w-6 text-[#4DB6AC]" />
                  </div>
                  <span className="text-xs bg-[#4DB6AC]/10 text-[#4DB6AC] px-2 py-1 rounded-full font-medium">
                    Ativos
                  </span>
                </div>
                <p className="text-3xl font-bold text-[#1C1C1E]">{metrics?.active_subscriptions || 0}</p>
                <p className="text-sm text-[#8E8E93]">Assinaturas ativas</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Activity className="h-6 w-6 text-purple-600" />
                  </div>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                    {metrics?.readings_today || 0} hoje
                  </span>
                </div>
                <p className="text-3xl font-bold text-[#1C1C1E]">{metrics?.total_readings || 0}</p>
                <p className="text-sm text-[#8E8E93]">Total de leituras</p>
              </div>
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Conversion & Churn */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <h3 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Métricas de Conversão
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-[#8E8E93]">Taxa de conversão</span>
                      <span className="text-sm font-bold text-green-600">{metrics?.conversion_rate || 0}%</span>
                    </div>
                    <Progress value={metrics?.conversion_rate || 0} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-[#8E8E93]">Taxa de churn</span>
                      <span className="text-sm font-bold text-red-500">{metrics?.churn_rate || 0}%</span>
                    </div>
                    <Progress value={metrics?.churn_rate || 0} className="h-2 bg-red-100" />
                  </div>
                  <div className="pt-4 border-t border-[#E8E4D9]">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#8E8E93]">Reatividade média</span>
                      <span className="text-sm font-bold text-[#2E5C55]">{(metrics?.average_reactivity * 100 || 0).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Revenue by Plan */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <h3 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Receita por Plano
                </h3>
                <div className="space-y-3">
                  {metrics?.revenue_by_plan && Object.entries(metrics.revenue_by_plan).map(([plan, revenue]) => (
                    <div key={plan} className="flex items-center justify-between p-3 bg-[#FDFBF7] rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#2E5C55]"></div>
                        <span className="text-sm font-medium text-[#1C1C1E]">{plan}</span>
                        <span className="text-xs text-[#8E8E93]">({metrics.plan_counts?.[plan] || 0} assinantes)</span>
                      </div>
                      <span className="text-sm font-bold text-[#2E5C55]">{formatCurrency(revenue)}</span>
                    </div>
                  ))}
                  {(!metrics?.revenue_by_plan || Object.keys(metrics.revenue_by_plan).length === 0) && (
                    <p className="text-sm text-[#8E8E93] text-center py-4">Nenhuma assinatura ativa</p>
                  )}
                </div>
              </div>

              {/* Top Zones */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
                <h3 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Zonas Mais Detectadas
                </h3>
                <div className="space-y-3">
                  {metrics?.top_zones?.map(([zone, count], index) => (
                    <div key={zone} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#2E5C55] text-white text-xs flex items-center justify-center font-bold">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-sm text-[#1C1C1E]">{zone}</span>
                      <span className="text-sm font-medium text-[#8E8E93]">{count}x</span>
                    </div>
                  ))}
                  {(!metrics?.top_zones || metrics.top_zones.length === 0) && (
                    <p className="text-sm text-[#8E8E93] text-center py-4">Nenhuma leitura ainda</p>
                  )}
                </div>
              </div>
            </div>

            {/* Period Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E8E4D9]/50">
              <h3 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Atividade por Período
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-[#FDFBF7] rounded-xl">
                  <p className="text-2xl font-bold text-[#2E5C55]">{metrics?.readings_today || 0}</p>
                  <p className="text-sm text-[#8E8E93]">Leituras hoje</p>
                </div>
                <div className="text-center p-4 bg-[#FDFBF7] rounded-xl">
                  <p className="text-2xl font-bold text-[#2E5C55]">{metrics?.readings_week || 0}</p>
                  <p className="text-sm text-[#8E8E93]">Esta semana</p>
                </div>
                <div className="text-center p-4 bg-[#FDFBF7] rounded-xl">
                  <p className="text-2xl font-bold text-[#2E5C55]">{metrics?.readings_month || 0}</p>
                  <p className="text-sm text-[#8E8E93]">Este mês</p>
                </div>
                <div className="text-center p-4 bg-[#FDFBF7] rounded-xl">
                  <p className="text-2xl font-bold text-[#D4A373]">{metrics?.new_users_month || 0}</p>
                  <p className="text-sm text-[#8E8E93]">Novos usuários/mês</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9]/50 overflow-hidden">
              <div className="p-6 border-b border-[#E8E4D9]">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E8E93]" />
                    <Input 
                      placeholder="Buscar usuários..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 h-12 rounded-xl border-2 border-[#E8E4D9]"
                    />
                  </div>
                  <span className="text-sm text-[#8E8E93]">{filteredUsers.length} usuários</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FDFBF7]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Usuário</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Leituras</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Cadastro</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-t border-[#E8E4D9] hover:bg-[#FDFBF7]">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#1C1C1E]">{u.name}</p>
                            <p className="text-sm text-[#8E8E93]">{u.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1">
                            {u.blocked && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full w-fit">Bloqueado</span>
                            )}
                            {u.free_reading_used ? (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full w-fit">Trial usado</span>
                            ) : (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full w-fit">Trial disponível</span>
                            )}
                            {u.role === 'admin' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full w-fit">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-[#1C1C1E]">{u.readings_count || 0}</td>
                        <td className="p-4 text-[#8E8E93]">{formatDate(u.created_at)}</td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={u.blocked ? "default" : "destructive"}
                              onClick={() => handleBlockUser(u.id, u.blocked)}
                              className="h-8 text-xs"
                            >
                              <Ban className="h-3 w-3 mr-1" />
                              {u.blocked ? 'Desbloquear' : 'Bloquear'}
                            </Button>
                            {u.free_reading_used && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetFreeTrial(u.id)}
                                className="h-8 text-xs"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Reset Trial
                              </Button>
                            )}
                          </div>
                          {/* Ativar Plano Manual */}
                          <div className="flex gap-2 mt-2">
                            <Select
                              value={activatingPlan === u.id ? selectedPlan : ''}
                              onValueChange={(value) => {
                                setActivatingPlan(u.id);
                                setSelectedPlan(value);
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs w-32">
                                <SelectValue placeholder="Ativar plano" />
                              </SelectTrigger>
                              <SelectContent>
                                {PLANS.map((plan) => (
                                  <SelectItem key={plan.id} value={plan.id}>
                                    {plan.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {activatingPlan === u.id && selectedPlan && (
                              <Button
                                size="sm"
                                className="h-8 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => handleActivatePlan(u.id, selectedPlan)}
                              >
                                <Gift className="h-3 w-3 mr-1" />
                                Ativar
                              </Button>
                            )}
                          </div>
                          {/* Mostrar plano ativo */}
                          {u.subscription && (
                            <div className="mt-2">
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                <CheckCircle className="h-3 w-3 inline mr-1" />
                                {PLANS.find(p => p.id === u.subscription.plan_id)?.name || u.subscription.plan_id}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Receipts Tab */}
          <TabsContent value="receipts">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9]/50 overflow-hidden">
              <div className="p-6 border-b border-[#E8E4D9]">
                <h3 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Comprovantes Pendentes ({receipts.length})
                </h3>
                <p className="text-sm text-[#8E8E93] mt-1">
                  Verifique os comprovantes de pagamento enviados pelos clientes
                </p>
              </div>
              
              {receipts.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-[#8E8E93]">Nenhum comprovante pendente de verificação</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E8E4D9]">
                  {receipts.map((receipt) => (
                    <div key={receipt.id} className="p-6 hover:bg-[#FDFBF7]">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-[#1C1C1E]">{receipt.user_name || 'Usuário'}</span>
                            <span className="text-sm text-[#8E8E93]">{receipt.user_email}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="bg-[#D4A373]/10 text-[#D4A373] px-3 py-1 rounded-full font-medium">
                              {receipt.plan_name}
                            </span>
                            <span className="text-[#2E5C55] font-medium">
                              R$ {receipt.plan_price?.toFixed(2).replace('.', ',')}
                            </span>
                            <span className="text-[#8E8E93]">
                              {formatDate(receipt.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/uploads/${receipt.receipt_filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#2E5C55] hover:underline flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            Ver
                          </a>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApproveReceipt(receipt.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleRejectReceipt(receipt.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rejeitar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9]/50 overflow-hidden">
              <div className="p-6 border-b border-[#E8E4D9]">
                <h3 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Assinaturas ({subscriptions.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FDFBF7]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">ID</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Plano</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Criado em</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-t border-[#E8E4D9] hover:bg-[#FDFBF7]">
                        <td className="p-4 text-sm text-[#8E8E93] font-mono">{sub.id?.slice(0, 8)}...</td>
                        <td className="p-4 font-medium text-[#1C1C1E]">{sub.plan_id}</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            sub.status === 'active' ? 'bg-green-100 text-green-700' :
                            sub.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="p-4 text-[#8E8E93]">{formatDate(sub.created_at)}</td>
                        <td className="p-4 font-medium text-[#2E5C55]">{formatCurrency(sub.price || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Readings Tab */}
          <TabsContent value="readings">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9]/50 overflow-hidden">
              <div className="p-6 border-b border-[#E8E4D9]">
                <h3 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Leituras Recentes ({readings.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FDFBF7]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">ID</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Usuário</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Zonas</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Reatividade</th>
                      <th className="text-left p-4 text-sm font-medium text-[#8E8E93]">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readings.slice(0, 50).map((reading) => (
                      <tr key={reading.id} className="border-t border-[#E8E4D9] hover:bg-[#FDFBF7]">
                        <td className="p-4 text-sm text-[#8E8E93] font-mono">{reading.id?.slice(0, 8)}...</td>
                        <td className="p-4 text-sm text-[#8E8E93] font-mono">{reading.user_id?.slice(0, 8)}...</td>
                        <td className="p-4 text-[#1C1C1E]">{reading.zones?.length || 0} zonas</td>
                        <td className="p-4">
                          <span className={`text-sm font-medium ${
                            reading.reactivity_score >= 0.7 ? 'text-red-500' :
                            reading.reactivity_score >= 0.4 ? 'text-amber-500' :
                            'text-green-500'
                          }`}>
                            {Math.round((reading.reactivity_score || 0) * 100)}%
                          </span>
                        </td>
                        <td className="p-4 text-[#8E8E93]">{formatDate(reading.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
